import { useMemo, useState, KeyboardEvent, useRef, useEffect } from 'react'
import clsx from 'clsx'

import { RadialBackground } from '@/ui/RadialBackground'
import { Input } from '@/ui/Input'
import { Loader } from '@/ui/Loader'
import { Garage } from '@/types/garage'
import { useGarageStore } from '../store'
import { useGetGarages } from '../api/useGetGarages'

import styles from './FindGarage.module.scss'
import { subDays } from 'date-fns'
import { useDateStore } from '@/components/DateInput'

export const FindGarage = () => {
    const { setDate } = useDateStore()
    const [search, setSearch] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

    const { data, isLoading } = useGetGarages()
    const { setGarage, setSelectedRoutes } = useGarageStore()

    const filteredGarages = useMemo(() => {
        if (!data) {
            return []
        }

        const garages = [...data]
        garages.sort((itemA, itemB) => itemA.name.localeCompare(itemB.name))

        return search
            ? garages.filter(
                  (item) =>
                      item.name.toLowerCase().includes(search.toLowerCase()) ||
                      item.shortCode.toLowerCase().includes(search.toLowerCase())
              )
            : garages
    }, [search, data])

    const handleSelectGarage = (garage: Garage) => {
        setGarage(garage)
        setDate(subDays(new Date(), 1))
        setSelectedRoutes(garage.routes)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedIndex((prev) => (prev === null || prev === filteredGarages.length - 1 ? 0 : prev + 1))
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedIndex((prev) => (prev === null || prev === 0 ? filteredGarages.length - 1 : prev - 1))
        }
        if (e.key === 'Enter' && highlightedIndex !== null) {
            e.preventDefault()
            handleSelectGarage(filteredGarages[highlightedIndex])
        }
    }

    useEffect(() => {
        if (highlightedIndex !== null && itemRefs.current[highlightedIndex]) {
            itemRefs.current[highlightedIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        }
    }, [highlightedIndex])

    return (
        <RadialBackground className={styles.background} variant="main">
            <section className={styles.root}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>Find a garage</h1>
                        <p className={styles.description}>Search for a garage to view its controller logs</p>
                    </div>
                    {isLoading && <Loader />}
                    {data && (
                        <>
                            <div className={styles.searchContainer}>
                                <div className={styles.inputContainer}>
                                    <Input
                                        fullWidth
                                        placeholder="Type to search or select garage"
                                        allowClear
                                        hidePlaceholderOnFocus
                                        onChange={(event) => setSearch(event.target.value)}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                                <div className={styles.listContainer}>
                                    <div className={styles.list}>
                                        {filteredGarages.map((item, index) => (
                                            <button
                                                key={item.shortCode}
                                                ref={(el) => (itemRefs.current[index] = el)}
                                                type="button"
                                                className={clsx(styles.item, {
                                                    [styles.highlighted]: highlightedIndex === index,
                                                })}
                                                onClick={() => handleSelectGarage(item)}
                                            >
                                                <span>{item.name}</span>
                                                <span className={styles.hint}>{item.shortCode}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </RadialBackground>
    )
}
